import React from 'react';
import { api } from '../api/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from "../components/ui/button";
import { Plus, Target, CheckCircle, TrendingUp, Award } from 'lucide-react';
import { motion } from "framer-motion";
import { useNavigate } from 'react-router-dom';


import StatsCard from '../components/dashboard/stats';
import TodaysTasks from '../components/dashboard/today_tasks';
import ActiveGoals from '../components/dashboard/activegoals';
import StreakCounter from '../components/streak';

export default function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: tasks = [] } = useQuery({
    queryKey: ['tasks'],
    queryFn: api.tasks.list,
  });

  const { data: goals = [] } = useQuery({
    queryKey: ['goals'],
    queryFn: api.goals.list,
  });

  const { data: progressData = [] } = useQuery({
    queryKey: ['userProgress'],
    queryFn: () => api.userProgress.get('default'), 
  });

  const userProgress = progressData[0] || {
    total_points: 0,
    current_streak: 0,
    longest_streak: 0,
    total_tasks_completed: 0,
    total_goals_completed: 0
  };

  const completeTaskMutation = useMutation({
    mutationFn: async (task) => {
      await api.tasks.update(task._id, {
        status: 'completed',
        completed_date: new Date().toISOString()
      });

      const updatedProgress = {
        ...userProgress,
        total_points: (userProgress.total_points || 0) + (task.points || 10),
        total_tasks_completed: (userProgress.total_tasks_completed || 0) + 1
      };

      if (userProgress._id) {
         await api.userProgress.update(userProgress._id, updatedProgress);
      } else {
         await api.userProgress.create(updatedProgress);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['userProgress'] });
    },
  });

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const activeGoals = goals.filter(g => g.status === 'active');

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Welcome back!</h1>
          <p className="text-slate-500">Let's make today productive.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatsCard icon={CheckCircle} label="Tasks Completed" value={userProgress.total_tasks_completed || 0} color="bg-green-500" />
          <StatsCard icon={Target} label="Active Goals" value={activeGoals.length} color="bg-blue-500" delay={0.1} />
          <StatsCard icon={TrendingUp} label="Total Points" value={userProgress.total_points || 0} color="bg-amber-500" delay={0.2} />
          <StatsCard icon={Award} label="Achievements" value={userProgress.achievements?.length || 0} color="bg-purple-500" delay={0.3} />
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mb-8">
          <StreakCounter currentStreak={userProgress.current_streak || 0} longestStreak={userProgress.longest_streak || 0} />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}>
            <TodaysTasks 
              tasks={pendingTasks} 
              onComplete={(task) => completeTaskMutation.mutate(task)} 
              onViewAll={() => navigate('/tasks')} 
            />
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}>
            <ActiveGoals 
              goals={goals} 
              onViewAll={() => navigate('/goals')} 
            />
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="mt-8 flex flex-wrap gap-4">
          <Button onClick={() => navigate('/tasks')} className="bg-blue-600 hover:bg-blue-700" size="lg">
            <Plus className="w-5 h-5 mr-2" /> New Task
          </Button>
          <Button onClick={() => navigate('/goals')} className="bg-indigo-600 hover:bg-indigo-700" size="lg">
            <Target className="w-5 h-5 mr-2" /> New Goal
          </Button>
        </motion.div>
      </div>
    </div>
  );
}